import { Box, PersonStanding, Redo2, RotateCcw, Save, Settings, Sparkles, Undo2, Users } from "lucide-react";
import { InviteButton } from "./InviteButton";
import {
  hasRemainingSteps,
  reopenChecklist,
  useCollabOnboarding,
} from "../lib/collabOnboarding";
import {
  hasRemainingFengShuiSteps,
  reopenFengShuiGuide,
  useFengShuiOnboarding,
} from "../lib/fengShuiOnboarding";
import {
  hasRemainingRender3dSteps,
  reopenRender3dGuide,
  useRender3dOnboarding,
} from "../lib/render3dOnboarding";
import { Segmented } from "./ui/Segmented";
import { isUnstartedRoom, useDesign } from "../lib/store";
import { useAuth, isEmulatorMode, hasAccount } from "../lib/auth";
import { useHasUnsavedChanges } from "../lib/unsavedChanges";
import { Button } from "./ui/Button";
import { useState } from "react";
import { Sheet } from "./ui/Sheet";
import { NumberStepper } from "./ui/NumberStepper";
import { formatLength } from "../lib/geom";
import {
  clearPremiumOverride,
  getPremiumOverride,
  PREMIUM_FEATURES,
  setPremiumOverride,
  usePremium,
  type PremiumFeatureKey,
} from "../lib/premium";
import { AuthMenu } from "./AuthMenu";
import { RoomsSheet } from "./RoomsSheet";
import { ShareSheet } from "./ShareSheet";
import { PresenceAvatars } from "./PresenceAvatars";
import { UsageMeter } from "./UsageMeter";
import { CancelSubscription } from "./CancelSubscription";
import { PremiumUpsellOverlay } from "./PremiumUpsellOverlay";
import { SharedRoomBanner } from "./SharedRoomBanner";
import { cn } from "../lib/cn";
import { setTopbarSlot } from "../lib/topbarSlot";

export function TopBar() {
  const view = useDesign((s) => s.view);
  const setView = useDesign((s) => s.setView);
  // Design/Walls only make sense once the room has an outline — without
  // walls or floor area there's nothing to place furniture inside or
  // mount things to. Gating the segmented control keeps users in the
  // Shape step until they've sketched or picked a preset, without ever
  // showing a "you can't do that yet" message.
  const roomUnstarted = useDesign((s) => isUnstartedRoom(s.design.room));
  const unit = useDesign((s) => s.design.unit);
  const setUnit = useDesign((s) => s.setUnit);
  const undo = useDesign((s) => s.undo);
  const redo = useDesign((s) => s.redo);
  const canUndo = useDesign(
    (s) => s.undoStack.length > 0 || s.historyPending
  );
  const canRedo = useDesign((s) => s.redoStack.length > 0);
  const person = useDesign((s) => s.design.person);
  const setPerson = useDesign((s) => s.setPerson);
  const resetAll = useDesign((s) => s.resetAll);

  const [showPerson, setShowPerson] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showRooms, setShowRooms] = useState(false);
  const [showShare, setShowShare] = useState(false);
  // When set, opens the Premium upsell overlay. Triggered from the
  // UsageMeter "Upgrade" button when a free user wants more credits.
  // Tracked here (rather than in DesignView) so the overlay can also
  // close the Settings sheet — otherwise the user would have to manually
  // close two sheets after returning from Stripe Checkout.
  const [settingsUpsell, setSettingsUpsell] =
    useState<PremiumFeatureKey | null>(null);
  const { status: authStatus, user: authUser } = useAuth();
  const hasUnsavedChanges = useHasUnsavedChanges();
  const collab = useCollabOnboarding();
  const fengShui = useFengShuiOnboarding();
  const fengShuiGuideReopenable =
    fengShui.active && fengShui.dismissed && hasRemainingFengShuiSteps(fengShui);
  const render3d = useRender3dOnboarding();
  const render3dGuideReopenable =
    render3d.active && render3d.dismissed && hasRemainingRender3dSteps(render3d);
  const guideReopenable =
    (collab.active && collab.dismissed && hasRemainingSteps(collab)) ||
    fengShuiGuideReopenable ||
    render3dGuideReopenable;
  // Only surface the unsaved-changes indicator to signed-in users, since
  // saving to the library is gated behind sign-in.
  const showUnsavedBadge = authStatus === "signed-in" && hasAccount(authUser) && hasUnsavedChanges;

  return (
    <header className="absolute top-0 inset-x-0 z-30 bg-ink-50/85 backdrop-blur-md border-b border-ink-200/70">
      <div className="pl-[max(0.5rem,var(--safe-left))] pr-[max(0.5rem,var(--safe-right))] sm:pl-[max(1.25rem,var(--safe-left))] sm:pr-[max(1.25rem,var(--safe-right))] pt-[calc(env(safe-area-inset-top)+0.5rem)] pb-2 land:pt-[calc(env(safe-area-inset-top)+0.25rem)] land:pb-1 flex items-center gap-1.5 sm:gap-2">
        {/* Left cluster: logo, Invite, then the shared-room chip. Invite
            sits next to the mark so collaboration stays a primary action.
            The logo hides below 440px so phones keep room for Undo/Redo;
            the cloud chip is icon-only below md. */}
        <div className="flex items-center gap-1.5 min-w-0 shrink-0">
          <div className="hidden min-[440px]:flex land:hidden items-center gap-2 min-w-0">
            <div className="size-9 shrink-0 rounded-xl bg-ink-900 text-white flex items-center justify-center">
              <svg viewBox="0 0 32 32" className="size-5">
                <path
                  d="M6 10 L20 10 L20 6 L26 6 L26 22 L18 22 L18 26 L6 26 Z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinejoin="round"
                />
                <circle cx="13" cy="18" r="1.6" fill="currentColor" />
              </svg>
            </div>
            {/* App name only renders at md+; on mobile the logo alone keeps
                enough horizontal room for Invite and the icon cluster. */}
            <div className="hidden md:block min-w-0">
              <div className="text-body font-semibold text-ink-900 leading-tight truncate">
                Room Canvas
              </div>
              <div className="text-caption text-ink-400 leading-tight">
                Design your space
              </div>
            </div>
          </div>
          <InviteButton onOpenShareSheet={() => setShowShare(true)} />
          <SharedRoomBanner />
        </div>

        {/* Center segmented view-switcher (desktop). Phones switch modes
            from the bottom `MobileModeBar` instead. */}
        <div className="hidden md:flex mx-auto min-w-0 justify-center">
          <Segmented
            value={view}
            onChange={(v) => setView(v)}
            options={[
              { id: "build", label: "Shape" },
              {
                id: "design",
                label: "Design",
                disabled: roomUnstarted,
                title: roomUnstarted
                  ? "Sketch a room outline first"
                  : undefined,
              },
              {
                id: "wall",
                label: "Walls",
                disabled: roomUnstarted,
                title: roomUnstarted
                  ? "Sketch a room outline first"
                  : undefined,
              },
            ]}
            size="sm"
          />
        </div>

        {/* Landscape phones: the active view's tool row lives here so the
            short canvas stays clear (see `topbarSlot.ts`). */}
        <div
          ref={setTopbarSlot}
          className="hidden land:flex mx-auto min-w-0 items-center justify-center gap-1.5"
          data-testid="topbar-tools-slot"
        />

        {/* Right cluster. Tap targets are 44×44 (Apple HIG / WCAG 2.5.5),
            40 below 360px. Phones have no keyboard shortcut, so Undo is
            always here; Redo joins it only once there's something to redo. */}
        <div className="ml-auto md:ml-0 land:ml-0 flex items-center shrink-0">
          <PresenceAvatars />
          <button
            onClick={undo}
            disabled={!canUndo}
            aria-label="Undo"
            data-testid="topbar-undo"
            className="h-11 w-10 min-[360px]:w-11 inline-flex items-center justify-center rounded-xl text-ink-700 hover:bg-ink-100 active:bg-ink-200 transition-colors disabled:opacity-40 disabled:pointer-events-none"
          >
            <Undo2 className="size-4" />
          </button>
          <button
            onClick={redo}
            disabled={!canRedo}
            aria-label="Redo"
            data-testid="topbar-redo"
            className={cn(
              "h-11 w-10 min-[360px]:w-11 items-center justify-center rounded-xl text-ink-700 hover:bg-ink-100 active:bg-ink-200 transition-colors disabled:opacity-40 disabled:pointer-events-none",
              canRedo ? "inline-flex" : "hidden md:inline-flex"
            )}
          >
            <Redo2 className="size-4" />
          </button>
          <button
            onClick={() => setShowPerson(true)}
            aria-label="Person"
            className="h-11 w-11 hidden md:inline-flex items-center justify-center rounded-xl text-ink-700 hover:bg-ink-100 active:bg-ink-200 transition-colors"
          >
            <PersonStanding className="size-4" />
          </button>
          <button
            onClick={() => setShowRooms(true)}
            aria-label="My rooms"
            title="My rooms"
            data-tour="my-rooms-button"
            className="relative h-11 w-10 min-[360px]:w-11 inline-flex items-center justify-center rounded-xl text-ink-700 hover:bg-ink-100 active:bg-ink-200 transition-colors"
          >
            <Save className="size-4" />
            {showUnsavedBadge && (
              <span
                aria-label="Unsaved changes"
                title="Unsaved changes"
                className="absolute top-1.5 right-1.5 size-2 rounded-full bg-orange-500 ring-2 ring-ink-50/85"
              />
            )}
          </button>
          <button
            onClick={() => setShowSettings(true)}
            aria-label="Settings"
            title="Settings"
            className="h-11 w-10 min-[360px]:w-11 inline-flex items-center justify-center rounded-xl text-ink-700 hover:bg-ink-100 active:bg-ink-200 transition-colors"
          >
            <Settings className="size-4" />
          </button>
          <AuthMenu />
        </div>
      </div>

      <RoomsSheet open={showRooms} onClose={() => setShowRooms(false)} />
      <ShareSheet open={showShare} onClose={() => setShowShare(false)} />

      <Sheet open={showPerson} onClose={() => setShowPerson(false)} title="Your scale">
        <p className="text-sm text-ink-500 mb-4">
          We render a silhouette in the room so you can feel how much space is around you.
        </p>
        <div className="flex flex-col gap-4">
          <NumberStepper
            label="Height"
            value={person.heightInches}
            onChange={(v) => setPerson({ heightInches: v })}
            min={36}
            max={96}
            step={1}
            suffix={unit === "imperial" ? "in" : "in"}
          />
          <div className="text-xs text-ink-400 -mt-2">
            ≈ {formatLength(person.heightInches, unit)}
          </div>
          <NumberStepper
            label="Weight"
            value={person.weightLbs}
            onChange={(v) => setPerson({ weightLbs: v })}
            min={60}
            max={500}
            step={5}
            suffix="lb"
          />
        </div>
      </Sheet>

      <PremiumUpsellOverlay
        open={settingsUpsell !== null}
        feature={settingsUpsell ?? "ai_layout_feng_shui"}
        trigger="manual"
        onClose={() => setSettingsUpsell(null)}
      />
      <Sheet open={showSettings} onClose={() => setShowSettings(false)} title="Settings">
        <div className="flex flex-col gap-5">
          <div>
            <div className="text-xs font-medium text-ink-500 mb-2">Units</div>
            <Segmented
              value={unit}
              onChange={(v) => setUnit(v)}
              options={[
                { id: "imperial", label: "Imperial (ft/in)" },
                { id: "metric", label: "Metric (cm/m)" },
              ]}
              size="sm"
            />
          </div>
          <UsageMeter
            onUpgradeClick={() => {
              // Drop the Settings sheet first so the upsell overlay isn't
              // stacked on top of it — backdrop clicks would close the
              // wrong layer and the visual depth feels muddy.
              setShowSettings(false);
              setSettingsUpsell("ai_layout_feng_shui");
            }}
          />
          {guideReopenable && (
            <div className="border-t border-ink-100 pt-4">
              <div className="text-xs font-medium text-ink-500 mb-2">
                {render3dGuideReopenable ? "3D walk-through" : fengShuiGuideReopenable ? "Feng Shui" : "Design together"}
              </div>
              <Button
                variant="secondary"
                className="w-full"
                onClick={() => {
                  if (render3dGuideReopenable) reopenRender3dGuide();
                  else if (fengShuiGuideReopenable) reopenFengShuiGuide();
                  else reopenChecklist();
                  setShowSettings(false);
                }}
                data-testid="settings-reopen-guide"
              >
                {render3dGuideReopenable ? (
                  <Box className="size-4" />
                ) : fengShuiGuideReopenable ? (
                  <Sparkles className="size-4" />
                ) : (
                  <Users className="size-4" />
                )}
                Getting started guide
              </Button>
            </div>
          )}
          <CancelSubscription />
          <PremiumOverrideSection />
          <div className="border-t border-ink-100 pt-4">
            <div className="text-xs font-medium text-ink-500 mb-2">Reset</div>
            <Button
              variant="danger"
              onClick={() => {
                if (confirm("Clear room and start fresh?")) {
                  resetAll();
                  setShowSettings(false);
                }
              }}
            >
              <RotateCcw className="size-4" />
              Start over
            </Button>
          </div>
        </div>
      </Sheet>
    </header>
  );
}

/**
 * Developer Premium override. Visible in dev builds, or in prod when the
 * page is loaded with `?dev=1`. Flips the client-side `usePremium` result
 * so QA can validate the upsell + Premium UI without paying for a real
 * Stripe subscription. Against production functions the server still
 * meters the real `stripeRole` claim (free users at cap get
 * `FREE_QUOTA_EXCEEDED`). Against the Auth emulator the toggle also
 * stamps that claim so local AI callables honor it.
 */
function PremiumOverrideSection() {
  const premium = usePremium();
  // Reading the override directly (rather than from a hook) means this
  // re-renders only when `usePremium()` changes — good enough since the
  // override mutation also publishes a new state.
  const overrideValue = getPremiumOverride();

  const isDev = import.meta.env.DEV;
  const isUrlDev =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("dev") === "1";
  if (!isDev && !isUrlDev) return null;

  const emulator = isEmulatorMode();
  const features = Object.values(PREMIUM_FEATURES);
  return (
    <div className="border-t border-ink-100 pt-4">
      <div className="text-xs font-medium text-ink-500 mb-1">Premium override</div>
      <div className="text-caption text-ink-400 mb-3">
        {emulator ? (
          <>
            Force the entitlement on or off locally. This stamps{" "}
            <code className="bg-ink-100 px-1 rounded">stripeRole</code> on
            the Auth emulator user so AI callables treat you as Premium.
            URL <code className="bg-ink-100 px-1 rounded">?premium=1</code>{" "}
            works too.
          </>
        ) : (
          <>
            Force the entitlement on or off locally. URL{" "}
            <code className="bg-ink-100 px-1 rounded">?premium=1</code> works
            too. The server still checks the real{" "}
            <code className="bg-ink-100 px-1 rounded">stripeRole</code> claim,
            so AI quota (including Import from URL) is unchanged.
          </>
        )}
      </div>
      <div
        className="flex items-center justify-between gap-3 p-3 rounded-xl bg-ink-50 ring-1 ring-ink-100"
        data-testid="premium-override-row"
      >
        <div className="min-w-0">
          <div className="text-body-sm font-semibold text-ink-900 truncate">
            Premium
          </div>
          <div className="text-caption text-ink-500">
            Status:{" "}
            <span className="font-semibold text-ink-700">{premium.status}</span>
            {premium.overridden && (
              <span className="ml-1.5 inline-flex items-center px-1.5 rounded-full bg-accent-100 text-accent-700 text-label font-semibold">
                override
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            onClick={() => setPremiumOverride(!premium.isPremium)}
            role="switch"
            aria-checked={premium.isPremium}
            aria-label="Toggle Premium override"
            data-testid="premium-override-toggle"
            className={`relative h-6 w-11 rounded-full transition-colors ${
              premium.isPremium ? "bg-ink-900" : "bg-ink-300"
            }`}
          >
            <span
              className={`absolute top-0.5 size-5 rounded-full bg-white shadow transition-all ${
                premium.isPremium ? "left-[22px]" : "left-0.5"
              }`}
            />
          </button>
          {overrideValue !== null && (
            <button
              onClick={() => clearPremiumOverride()}
              className="text-caption text-ink-500 hover:text-ink-900 px-1.5"
              title="Clear local override"
            >
              reset
            </button>
          )}
        </div>
      </div>
      <p className="mt-2 text-caption text-ink-400">
        Includes:{" "}
        {features.map((f, i) => (
          <span key={f.label}>
            {i > 0 && ", "}
            {f.label}
          </span>
        ))}
      </p>
    </div>
  );
}
